import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";

/**
 * IsaiahPlacementManager v3 (Phase 4)
 * React Context + Hook that manages where Isaiah appears and his state.
 * Phase 3: World-aware context, arcade, adventure, creator worlds
 * Phase 4: World transition tracking, previous world memory, brand shimmer
 *
 * Visibility rules:
 * - Show on: homepage, main landing pages, dashboards, hub areas
 * - Hide on: active gameplay, timed challenges, full-screen activities, quizzes
 * - Minimize on: builder moments, concentrated interaction pages
 */

const IsaiahContext = createContext(null);

// Routes where the floating companion should be HIDDEN entirely
const HIDDEN_ROUTES = [
  /^\/arcade\/[^/]+$/, // Active game pages (e.g. /arcade/chess)
  /^\/arcade\/family\/[^/]+$/, // Active family game pages
  /^\/learn\/tutor$/,   // AI Tutor conversation
  /^\/quiz/,            // Quiz pages
];

// Routes where companion should be MINIMIZED (collapsed, available on tap)
const MINIMIZED_ROUTES = [
  /^\/art-creator/,
  /^\/art-studio/,
  /^\/adventures\/ai-storyteller/,
  /^\/puzzle-creator/,
  /^\/world-builder/,
  /^\/mini-game-studio/,
  /^\/adventure-builder/,
  /^\/adventures\/episode\//,
];

// Routes where companion should be VISIBLE
const VISIBLE_ROUTES = [
  /^\/$/, // Homepage
  /^\/learn$/,
  /^\/arcade$/,
  /^\/adventures$/,
  /^\/creator-studio$/,
  /^\/art-studio$/,
  /^\/my-world/,
  /^\/learning-path/,
  /^\/trophies/,
  /^\/marketplace/,
  /^\/achievements/,
  /^\/leaderboard/,
  /^\/passport/,
  /^\/sky-passport/,
  /^\/events/,
  /^\/parent-corner/,
  /^\/community/,
  /^\/friends/,
  // Learning activity pages
  /^\/spelling-practice$/,
  /^\/math-practice$/,
  /^\/math-quest$/,
  /^\/math-help$/,
  /^\/read-with-isaiah$/,
  /^\/reading-fluency$/,
  /^\/word-explorer$/,
  /^\/speed-typer$/,
  /^\/upload-learn$/,
  /^\/tutor$/,
  /^\/french$/,
  /^\/spanish$/,
];

// Determine page context from pathname
const getPageContext = (pathname) => {
  if (pathname === "/") return "home";
  if (pathname.match(/^\/(spelling|math|read-with|reading-fluency|word-explorer|speed-typer|quiz|pattern|tutor|upload-learn|french|spanish)/)) return "learn-activity";
  if (pathname.startsWith("/learn")) return "learn";
  if (pathname.startsWith("/arcade")) return "arcade";
  if (pathname.startsWith("/adventure")) return "adventure";
  if (pathname.startsWith("/creator-studio")) return "creator";
  if (pathname.startsWith("/art")) return "art";
  return "default";
};

// Determine which world the user is in (for Phase 3 world roles)
const getWorldId = (pathname) => {
  if (pathname.startsWith("/arcade")) return "arcade";
  if (pathname.startsWith("/adventure")) return "adventure";
  if (pathname.startsWith("/creator-studio")) return "creator";
  if (pathname.startsWith("/learn")) return "learn";
  if (pathname.startsWith("/art")) return "art";
  return null;
};

// Determine companion visibility mode
const getVisibilityMode = (pathname) => {
  if (HIDDEN_ROUTES.some((r) => r.test(pathname))) return "hidden";
  if (MINIMIZED_ROUTES.some((r) => r.test(pathname))) return "minimized";
  if (VISIBLE_ROUTES.some((r) => r.test(pathname))) return "visible";
  return "minimized";
};

export const IsaiahProvider = ({ children }) => {
  const location = useLocation();
  const [companionPose, setCompanionPose] = useState("idle");
  const [companionMessage, setCompanionMessage] = useState(null);
  const [heroContext, setHeroContext] = useState(null);
  const [userDismissed, setUserDismissed] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevWorldRef = useRef(null);

  const pageContext = getPageContext(location.pathname);
  const worldId = getWorldId(location.pathname);
  const visibilityMode = getVisibilityMode(location.pathname);

  // Track world transitions for branded animations
  useEffect(() => {
    const newWorld = worldId;
    const prevWorld = prevWorldRef.current;

    if (prevWorld && newWorld && prevWorld !== newWorld) {
      // World-to-world transition detected
      setIsTransitioning(true);
      const timer = setTimeout(() => setIsTransitioning(false), 600);
      return () => clearTimeout(timer);
    }

    prevWorldRef.current = newWorld;
  }, [worldId]);

  // Reset dismiss state on route change
  useEffect(() => {
    setUserDismissed(false);
    setCompanionMessage(null);
  }, [location.pathname]);

  const showCompanionMessage = useCallback((msg, duration = 5000) => {
    setCompanionMessage(msg);
    if (duration > 0) {
      const t = setTimeout(() => setCompanionMessage(null), duration);
      return () => clearTimeout(t);
    }
  }, []);

  const dismissCompanion = useCallback(() => {
    setUserDismissed(true);
  }, []);

  const restoreCompanion = useCallback(() => {
    setUserDismissed(false);
  }, []);

  // Memoize context value so Isaiah consumers don't re-render on every unrelated
  // parent update, this was a secondary cause of the "flash/swap" feel on mount.
  const value = useMemo(() => ({
    pageContext,
    worldId,
    visibilityMode,
    companionPose,
    setCompanionPose,
    companionMessage,
    showCompanionMessage,
    heroContext,
    setHeroContext,
    userDismissed,
    dismissCompanion,
    restoreCompanion,
    isTransitioning,
    isCompanionVisible: visibilityMode === "visible" && !userDismissed,
    isCompanionMinimized: visibilityMode === "minimized" || userDismissed,
    isCompanionHidden: visibilityMode === "hidden",
  }), [
    pageContext, worldId, visibilityMode, companionPose, companionMessage,
    heroContext, userDismissed, isTransitioning, showCompanionMessage,
    dismissCompanion, restoreCompanion,
  ]);

  return (
    <IsaiahContext.Provider value={value}>
      {children}
    </IsaiahContext.Provider>
  );
};

export const useIsaiah = () => {
  const ctx = useContext(IsaiahContext);
  if (!ctx) {
    return {
      pageContext: "default",
      worldId: null,
      visibilityMode: "hidden",
      companionPose: "idle",
      setCompanionPose: () => {},
      companionMessage: null,
      showCompanionMessage: () => {},
      heroContext: null,
      setHeroContext: () => {},
      userDismissed: false,
      dismissCompanion: () => {},
      restoreCompanion: () => {},
      isTransitioning: false,
      isCompanionVisible: false,
      isCompanionMinimized: false,
      isCompanionHidden: true,
    };
  }
  return ctx;
};

export default IsaiahProvider;
